import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { FaArrowLeft, FaDownload, FaSyncAlt, FaClipboardCheck, FaFileAlt, FaBookmark, FaChartBar } from 'react-icons/fa';
import html2pdf from 'html2pdf.js';
import './Page.css';
import MaturityScorecard from '../reports/MaturityScorecard';

const ScorecardDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        
        // If ID is provided, fetch that specific assessment
        if (id) {
          const res = await axios.get(`/api/assessments/${id}`);
          
          // Process the assessment data to ensure we have category scores
          const processedAssessment = processAssessmentData(res.data);
          setAssessment(processedAssessment);
        } else {
          // Otherwise, get the most recent completed assessment
          const assessmentsRes = await axios.get('/api/assessments');
          const completedAssessments = assessmentsRes.data.filter(a => a.status === 'completed');
          
          if (completedAssessments.length > 0) {
            // Sort by date (newest first) and get the first one
            const sortedAssessments = completedAssessments.sort(
              (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
            );
            
            // Process the assessment data to ensure we have category scores
            const processedAssessment = processAssessmentData(sortedAssessments[0]);
            setAssessment(processedAssessment);
          } else {
            setError(t('reports.noCompletedAssessments'));
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching assessment for scorecard details:', err);
        setError(t('reports.errorFetchingData'));
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [id, t]);

  const handleDownloadPDF = async () => {
    try {
      setLoading(true);
      
      // Get the content to convert to PDF
      const element = document.getElementById('scorecard-details-content');
      
      if (!element) {
        throw new Error('Content element not found');
      }
      
      // Create a custom PDF with html2pdf
      const opt = {
        margin: [0.5, 0.5, 1.5, 0.5], // Increased bottom margin for footer
        filename: `Scorecard_Details_${assessment?.title ? assessment.title.replace(/\s+/g, '_') : 'Report'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'cm', format: 'a4', orientation: 'landscape' }, // Changed to landscape
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['tr', 'td', 'th', 'img', 'div.scorecard-section'] }
      };
      
      // Add custom footer to each page
      const footerText = `\u00a9 ${new Date().getFullYear()} Business Resilience Navigator. All rights reserved.`;
      
      const worker = html2pdf().from(element).set(opt);
      
      // Add footer to each page
      worker.toPdf().get('pdf').then((pdf) => {
        // Get total page count
        const totalPages = pdf.internal.getNumberOfPages();
        
        // Add footer to each page
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          
          // Add page number
          pdf.setFontSize(10);
          pdf.setTextColor(76, 84, 84); // #4C5454
          
          // Add page number first
          pdf.text(
            `Page ${i} of ${totalPages}`,
            pdf.internal.pageSize.getWidth() / 2,
            pdf.internal.pageSize.getHeight() - 0.5,
            { align: 'center' }
          );
          
          // Add footer text
          pdf.setFontSize(8);
          pdf.text(
            footerText,
            pdf.internal.pageSize.getWidth() / 2,
            pdf.internal.pageSize.getHeight() - 0.3,
            { align: 'center' }
          );
        }
        
        return pdf;
      }).save();
      
      setLoading(false);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Error generating PDF. Please try again.');
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch the assessment data again
      if (id) {
        const res = await axios.get(`/api/assessments/${id}?regenerate=true`);
        
        // Process the assessment data to ensure we have category scores
        const processedAssessment = processAssessmentData(res.data);
        setAssessment(processedAssessment);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error regenerating scorecard details:', err);
      setError('Failed to regenerate scorecard details');
      setLoading(false);
    }
  };
  
  // Function to get color based on score
  const getScoreColor = (score) => {
    if (score >= 91) return '#198754'; // Green - Optimized (91% - 100%)
    if (score >= 75) return '#0D6EFD'; // Blue - Compliant (75% - 90%)
    if (score >= 50) return '#F4D03F'; // Softer Yellow - Managed (50% - 74%)
    if (score >= 20) return '#E67E22'; // Softer Orange - Defined (20% - 49%)
    if (score >= 1) return '#DC3545';  // Red - Initial (1% - 19%)
    return '#6C757D';                  // Gray - None (0%)
  };

  // Function to get text color based on background color
  const getTextColor = (backgroundColor) => {
    // Dark backgrounds get white text
    const darkColors = ['#198754', '#0D6EFD', '#DC3545', '#6C757D'];
    if (darkColors.includes(backgroundColor)) {
      return '#FFFFFF';
    }
    // Yellow gets navy text
    if (backgroundColor === '#F4D03F') {
      return '#243444';
    }
    // Other light backgrounds get dark text
    return '#000000';
  };

  // Process assessment data to ensure we have category scores
  const processAssessmentData = (assessmentData) => {
    if (!assessmentData) return null;
    
    // Calculate category scores if they don't exist
    let categoryScores = assessmentData.categoryScores || {};
    let score = assessmentData.score || 0;
    
    // If category scores don't exist, calculate them
    if (Object.keys(categoryScores).length === 0 && assessmentData.responses) {
      const responses = assessmentData.responses;
      const categories = {};
      
      // Group responses by category
      responses.forEach(response => {
        if (!categories[response.category]) {
          categories[response.category] = [];
        }
        categories[response.category].push(response);
      });
      
      // Check if any categories should be excluded (marked as not applicable)
      const notApplicableCategories = assessmentData.notApplicableCategories || [];
      console.log('Not applicable categories:', notApplicableCategories);
      
      // Calculate score for each category
      Object.keys(categories).forEach(category => {
        // Skip categories marked as not applicable
        if (notApplicableCategories.includes(category)) {
          console.log(`Skipping category ${category} as it's marked not applicable`);
          return;
        }
        
        const categoryResponses = categories[category];
        // Only include responses that are not marked as notApplicable
        const validResponses = categoryResponses.filter(r => !r.notApplicable);
        const answeredResponses = validResponses.filter(r => r.answer && r.answer !== '');
        
        if (answeredResponses.length > 0) {
          const totalScore = answeredResponses.reduce((sum, r) => {
            const answer = parseInt(r.answer, 10);
            return sum + (isNaN(answer) ? 0 : answer);
          }, 0);
          
          categoryScores[category] = (totalScore / (answeredResponses.length * 5)) * 100;
        } else if (validResponses.length > 0) {
          // If there are valid responses but none are answered, score is 0
          categoryScores[category] = 0;
        }
        // If all responses in a category are marked as not applicable, don't include the category
      });
      
      // Calculate overall score only from categories that aren't marked as not applicable
      const validCategoryScores = Object.keys(categoryScores)
        .filter(category => !notApplicableCategories.includes(category));
      
      if (validCategoryScores.length > 0) {
        const totalCategoryScore = validCategoryScores.reduce(
          (sum, category) => sum + categoryScores[category], 0
        );
        score = totalCategoryScore / validCategoryScores.length;
      }
    }
    
    // Process responses to add normalized scores for each question
    let processedResponses = [];
    if (assessmentData.responses) {
      processedResponses = assessmentData.responses.map(response => {
        const answerValue = parseInt(response.answer, 10);
        const normalizedScore = isNaN(answerValue) ? 0 : (answerValue / 5) * 100;
        return {
          ...response,
          normalizedScore
        };
      });
    }
    
    return {
      ...assessmentData,
      categoryScores: categoryScores,
      score: score || 0,
      responses: processedResponses
    };
  };

  if (loading && !assessment) {
    return (
      <div className="page-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title-section">
          <button className="back-button" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back to Assessment
          </button>
          <h1 className="page-title">Scorecard Details</h1>
        </div>
        <div className="page-actions">
          <button className="action-button" onClick={handleDownloadPDF}>
            <FaDownload /> Download PDF
          </button>
          <button className="action-button" onClick={handleRegenerate}>
            <FaSyncAlt /> Regenerate
          </button>
        </div>
      </div>
      
      <div id="scorecard-details-content" style={{ maxWidth: '100%', pageBreakInside: 'avoid' }}>
        <style>
          {`
            @media print {
              .scorecard-container {
                width: 100%;
                max-width: 100%;
                page-break-inside: avoid;
              }
              .scorecard-section {
                page-break-inside: avoid;
              }
              .scorecard-table {
                page-break-inside: avoid;
                width: 100%;
              }
              .scorecard-row {
                page-break-inside: avoid;
              }
              .legend-container {
                page-break-before: avoid;
              }
              .question-details {
                page-break-inside: avoid;
              }
            }
            
            /* Make sure all content is visible for printing */
            .scorecard-container, .questions-section, .question-details {
              display: block !important;
            }
            
            /* Hide any Questions, Answers, Notes section */
            .questions-answers-section {
              display: none !important;
            }
            
            /* Styling for questions section */
            .questions-section {
              margin-top: 30px;
              border-top: 2px solid #D2BA7B;
              padding-top: 20px;
            }
            
            .category-header {
              background-color: #243444;
              color: #FFFAFA;
              padding: 10px 15px;
              margin: 20px 0 10px 0;
              border-radius: 4px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            
            .category-score {
              background-color: #243444;
              color: #FFFAFA;
              padding: 8px 15px;
              border-radius: 4px;
              font-weight: bold;
              font-size: 16px;
              min-width: 80px;
              text-align: center;
            }
            
            .question-details {
              background-color: #FFFAFA;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
              margin-bottom: 15px;
              padding: 15px;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            
            .question-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 10px;
              border-bottom: 1px solid #eee;
              padding-bottom: 10px;
            }
            
            .question-text {
              flex: 1;
              font-weight: bold;
              color: #243444;
            }
            
            .question-score {
              padding: 5px 10px;
              border-radius: 15px;
              font-weight: bold;
              margin-left: 10px;
              min-width: 60px;
              text-align: center;
            }
            
            .question-content {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 15px;
            }
            
            .question-answer, .question-notes, .question-standard {
              margin-bottom: 10px;
            }
            
            .content-label {
              font-weight: bold;
              color: #4C5454;
              margin-bottom: 5px;
              display: flex;
              align-items: center;
              gap: 5px;
            }
            
            .content-value {
              background-color: #f5f5f5;
              padding: 8px 12px;
              border-radius: 4px;
              border-left: 3px solid #D2BA7B;
            }
            
            .answer-description {
              font-style: italic;
              color: #666;
              margin-top: 5px;
            }
          `}
        </style>
        {error ? (
          <div className="error-message">
            <p>{error}</p>
            <button 
              className="primary-button"
              onClick={() => navigate('/assessments/new')}
            >
              {t('assessments.startNewAssessment')}
            </button>
          </div>
        ) : (
          <>
            {/* Maturity Scorecard Section */}
            <MaturityScorecard assessmentData={{
              ...assessment,
              organization: user?.organizationName || t('reports.yourOrganization'),
              industry: assessment?.industryType || user?.industryType || t('reports.notSpecified'),
              title: `${assessment?.title || t('reports.maturityAssessment')} - Scorecard Details`
            }} />

            <div className="scorecard-description">
              <h3>{t('reports.howToReadScorecard')}</h3>
              <p>{t('reports.scorecardDescription')}</p>
              <ul>
                <li><strong>{t('reports.none')}:</strong> {t('reports.noneDescription')}</li>
                <li><strong>{t('reports.initial')}:</strong> {t('reports.initialDescription')}</li>
                <li><strong>{t('reports.defined')}:</strong> {t('reports.definedDescription')}</li>
                <li><strong>{t('reports.managed')}:</strong> {t('reports.managedDescription')}</li>
                <li><strong>{t('reports.compliant')}:</strong> {t('reports.compliantDescription')}</li>
                <li><strong>{t('reports.optimized')}:</strong> {t('reports.optimizedDescription')}</li>
              </ul>
            </div>
            
            {/* Detailed Questions Section */}
            <div className="questions-section">
              <h2 style={{ color: '#243444', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaClipboardCheck /> Detailed Assessment Responses
              </h2>
              <p>This section provides a detailed breakdown of each question in the assessment, including the response provided, notes, applicable standards, and individual scores.</p>
              
              {assessment && assessment.responses && (
                <div>
                  {/* Group responses by category */}
                  {Object.entries(assessment.responses.reduce((acc, response) => {
                    if (!acc[response.category]) {
                      acc[response.category] = [];
                    }
                    acc[response.category].push(response);
                    return acc;
                  }, {})).map(([category, responses]) => {
                    // Calculate category score
                    const categoryScore = assessment.categoryScores[category] || 0;
                    const roundedScore = Math.round(categoryScore);
                    
                    return (
                      <div key={category}>
                        <div className="category-header">
                          <h3 style={{ margin: 0 }}>{category}</h3>
                          <div className="category-score" style={{ 
                            backgroundColor: getScoreColor(roundedScore),
                            color: getTextColor(getScoreColor(roundedScore))
                          }}>
                            Score: {roundedScore}%
                          </div>
                        </div>
                        
                        {responses.map((response, index) => {
                          const answerValue = parseInt(response.answer, 10);
                          const normalizedScore = isNaN(answerValue) ? 0 : Math.round((answerValue / 5) * 100);
                          const scoreColor = getScoreColor(normalizedScore);
                          
                          // Get answer description based on value
                          let answerDescription = '';
                          let answerText = '';
                          switch(answerValue) {
                            case 0:
                              answerDescription = 'This practice is not performed or is performed in an ad-hoc manner.';
                              answerText = 'No practice is consistently performed, tracked, and resourced across most of the organization.';
                              break;
                            case 1:
                              answerDescription = 'This practice is performed inconsistently and lacks documentation.';
                              answerText = 'This practice is performed inconsistently and lacks documentation.';
                              break;
                            case 2:
                              answerDescription = 'This practice is documented and repeatable in some areas but lacks enterprise-wide adoption.';
                              answerText = 'This practice is documented and repeatable in some areas but lacks enterprise-wide adoption.';
                              break;
                            case 3:
                              answerDescription = 'This practice is consistently performed, tracked, and resourced across most of the organization.';
                              answerText = 'This practice is consistently performed, tracked, and resourced across most of the organization.';
                              break;
                            case 4:
                              answerDescription = 'This practice fully meets industry and regulatory standards and is regularly audited for effectiveness.';
                              answerText = 'This practice fully meets industry and regulatory standards and is regularly audited for effectiveness.';
                              break;
                            case 5:
                              answerDescription = 'This practice is continuously improved, benchmarked, and embedded in strategic decision-making.';
                              answerText = 'This practice is continuously improved, benchmarked, and embedded in strategic decision-making.';
                              break;
                            default:
                              answerDescription = 'No answer provided.';
                              answerText = 'No answer provided.';
                          }
                          
                          return (
                            <div key={response.questionId || index} className="question-details">
                              <div className="question-header">
                                <div className="question-text">{response.question}</div>
                                <div 
                                  className="question-score" 
                                  style={{ 
                                    backgroundColor: scoreColor, 
                                    color: getTextColor(scoreColor)
                                  }}
                                >
                                  {normalizedScore}%
                                </div>
                              </div>
                              
                              <div className="question-content">
                                <div className="question-answer">
                                  <div className="content-label">
                                    <FaChartBar /> Answer
                                  </div>
                                  <div className="content-value">
                                    {answerText}
                                  </div>
                                </div>
                                
                                <div className="question-standard">
                                  <div className="content-label">
                                    <FaBookmark /> Applicable Standards
                                  </div>
                                  <div className="content-value">
                                    {response.standards || 'ISO 22301, NFPA 1600'}
                                  </div>
                                </div>
                                
                                <div className="question-notes" style={{ gridColumn: '1 / span 2' }}>
                                  <div className="content-label">
                                    <FaFileAlt /> Notes
                                  </div>
                                  <div className="content-value">
                                    {response.notes || 'No notes provided'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScorecardDetailsPage;
